import { useEffect, useState } from "react";
import { X, Calendar, Clock, MapPin, Video, CheckCircle } from "lucide-react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import { postInscription, getPaypalConfig, createPaypalOrder, capturePaypalOrder, formatApiError } from "@/lib/api";

const inputCls =
  "w-full rounded-sm border border-input bg-background px-4 py-3 text-sm outline-none transition-colors duration-200 focus:border-primary focus:ring-1 focus:ring-primary";

const estEnLigne = (f) => f.format.toLowerCase().includes("ligne") || f.format.toLowerCase().includes("hybride");
const estPresentiel = (f) => f.format.toLowerCase().includes("sentiel") || f.format.toLowerCase().includes("hybride");

export const InscriptionModal = ({ formation, onClose }) => {
  const [form, setForm] = useState({ nom: "", courriel: "", telephone: "" });
  const [etape, setEtape] = useState("formulaire");
  const [inscription, setInscription] = useState(null);
  const [config, setConfig] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    getPaypalConfig().then(setConfig).catch(() => setConfig({ active: false }));
  }, []);

  const gratuite = !formation.prix || formation.prix === 0;
  const update = (champ) => (e) => setForm({ ...form, [champ]: e.target.value });

  const soumettre = async (e) => {
    e.preventDefault();
    setEnvoi(true);
    try {
      const r = await postInscription({
        formation_id: formation.id,
        nom: form.nom,
        courriel: form.courriel,
        telephone: form.telephone || null,
      });
      if (r.statut === "confirmee") {
        setEtape("succes");
        toast.success("Inscription confirmée!", { description: "Un courriel de confirmation vous a été envoyé avec tous les détails." });
      } else {
        setInscription(r);
        setEtape("paiement");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setEnvoi(false);
    }
  };

  const Details = () => (
    <ul className="space-y-3 border border-border bg-card p-6 text-sm text-foreground/70" data-testid="inscription-details">
      <li className="flex items-center gap-3"><Calendar size={16} className="shrink-0 text-primary" /> {formation.prochaine_session}</li>
      <li className="flex items-center gap-3"><Clock size={16} className="shrink-0 text-primary" /> Durée : {formation.duree}</li>
      {estPresentiel(formation) && (
        <li className="flex items-center gap-3"><MapPin size={16} className="shrink-0 text-primary" /> {formation.adresse || formation.format}</li>
      )}
      {estEnLigne(formation) && (
        <li className="flex items-center gap-3"><Video size={16} className="shrink-0 text-primary" /> En ligne — le lien Zoom est inclus dans le courriel de confirmation</li>
      )}
      <li className="flex items-center gap-3 font-semibold text-foreground">
        {gratuite ? "Formation gratuite" : `Tarif : ${(formation.prix_effectif ?? formation.prix).toLocaleString("fr-CA")} $ CAD`}
      </li>
    </ul>
  );

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto bg-espresso/70 backdrop-blur-sm" data-testid="inscription-modal">
      <div className="mx-auto my-10 max-w-lg border border-border bg-background p-8 md:p-10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{formation.categorie}</p>
            <h2 className="mt-2 font-serif text-2xl font-semibold leading-snug md:text-3xl" data-testid="inscription-modal-title">
              {etape === "succes" ? "Inscription confirmée" : formation.titre}
            </h2>
          </div>
          <button onClick={onClose} aria-label="Fermer" data-testid="inscription-close-button" className="mt-1 shrink-0 text-foreground/50 transition-colors hover:text-primary">
            <X size={24} />
          </button>
        </div>

        {etape === "formulaire" && (
          <>
            <div className="mt-6"><Details /></div>
            <form onSubmit={soumettre} className="mt-6 space-y-5" data-testid="inscription-form">
              <div>
                <label htmlFor="i-nom" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Nom complet *</label>
                <input id="i-nom" required minLength={2} value={form.nom} onChange={update("nom")} className={inputCls} data-testid="inscription-nom-input" />
              </div>
              <div>
                <label htmlFor="i-courriel" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Courriel *</label>
                <input id="i-courriel" type="email" required value={form.courriel} onChange={update("courriel")} className={inputCls} data-testid="inscription-courriel-input" />
              </div>
              <div>
                <label htmlFor="i-tel" className="mb-2 block text-xs font-semibold uppercase tracking-[0.15em] text-foreground/60">Téléphone</label>
                <input id="i-tel" type="tel" value={form.telephone} onChange={update("telephone")} className={inputCls} data-testid="inscription-telephone-input" />
              </div>
              <button
                type="submit"
                disabled={envoi}
                data-testid="inscription-submit-button"
                className="w-full rounded-sm bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-[background-color,opacity] duration-200 hover:bg-[hsl(22,83%,40%)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {envoi ? "Inscription en cours…" : gratuite ? "Confirmer mon inscription" : `Continuer vers le paiement — ${(formation.prix_effectif ?? formation.prix).toLocaleString("fr-CA")} $ CAD`}
              </button>
            </form>
          </>
        )}

        {etape === "paiement" && inscription && (
          <div className="mt-6" data-testid="inscription-paiement">
            <p className="text-sm leading-relaxed text-foreground/70">
              Votre place est réservée, {inscription.nom.split(" ")[0]}. Il ne reste que le paiement
              de <span className="font-semibold text-foreground">{inscription.montant} $ CAD</span> pour
              confirmer votre inscription.
            </p>
            <div className="mt-6"><Details /></div>
            <div className="mt-8">
              {config?.active ? (
                <PayPalScriptProvider options={{ clientId: config.client_id, currency: "CAD" }}>
                  <PayPalButtons
                    style={{ layout: "vertical", color: "gold", shape: "rect", label: "pay" }}
                    data-testid="paypal-buttons"
                    createOrder={async () => {
                      const r = await createPaypalOrder(inscription.inscription_id);
                      return r.order_id;
                    }}
                    onApprove={async (data) => {
                      try {
                        await capturePaypalOrder(data.orderID);
                        setEtape("succes");
                        toast.success("Paiement confirmé!", { description: "Votre courriel de confirmation avec tous les détails est en route." });
                      } catch (err) {
                        toast.error(formatApiError(err.response?.data?.detail));
                      }
                    }}
                    onError={() => toast.error("Le paiement a échoué. Veuillez réessayer.")}
                  />
                </PayPalScriptProvider>
              ) : (
                <div className="border border-gold/40 bg-gold/10 p-6" data-testid="paiement-indisponible">
                  <p className="text-sm leading-relaxed text-foreground/70">
                    Le paiement en ligne est en cours d'activation. En attendant, écrivez-nous via la
                    page Contact et nous vous transmettrons les instructions de paiement (virement
                    Interac) pour finaliser votre inscription.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {etape === "succes" && (
          <div className="mt-6" data-testid="inscription-succes">
            <div className="flex items-start gap-4 border border-gold/40 bg-gold/10 p-6">
              <CheckCircle size={26} strokeWidth={1.5} className="mt-0.5 shrink-0 text-primary" />
              <div>
                <p className="font-semibold">À très bientôt, {form.nom.split(" ")[0]}!</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground/70">
                  Un courriel de confirmation a été envoyé à <span className="font-semibold">{form.courriel}</span> avec
                  tous les détails de la formation{estEnLigne(formation) ? ", incluant le lien Zoom" : ""}.
                </p>
              </div>
            </div>
            <div className="mt-6"><Details /></div>
            <button
              onClick={onClose}
              data-testid="inscription-done-button"
              className="mt-8 w-full rounded-sm border border-foreground/20 px-8 py-4 text-sm font-semibold transition-colors hover:border-primary hover:text-primary"
            >
              Fermer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
